import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  apiGetEmployeePerformance,
  apiGetEmployeeCareerPath,
  apiCreateKpi, apiUpdateKpi, apiDeleteKpi,
  apiCreateGoal, apiUpdateGoal, apiDeleteGoal,
  apiCreateReview, apiUpdateReview, apiDeleteReview,
  apiCreateCareerPath, apiUpdateCareerPath, apiDeleteCareerPath,
  apiGetDepartments,
  type PerformanceKpi, type PerformanceGoal, type PerformanceReview,
  type CareerPath, type Department,
} from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { FloatField, FloatWrapper, floatChildCls } from "@/components/ui/FormField";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Target, TrendingUp, Star, BarChart3, User, Calendar,
  CheckCircle2, Clock, AlertTriangle, Pencil, Trash2, Plus, Loader2, ChevronRight,
  GraduationCap, ArrowUpCircle, BookOpen, Lightbulb, ClipboardCheck,
} from "lucide-react";

// ─── Constants ────────────────────────────────────────────────────────────────

const RATING_LABELS: Record<number, string> = {
  5: "Outstanding",
  4: "Exceeds Expectations",
  3: "Meets Expectations",
  2: "Needs Improvement",
  1: "Unsatisfactory",
};

const ratingColorMap: Record<string, string> = {
  Outstanding: "bg-amber-100 text-amber-700 border-amber-200",
  "Exceeds Expectations": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Meets Expectations": "bg-blue-100 text-blue-700 border-blue-200",
  "Needs Improvement": "bg-orange-100 text-orange-700 border-orange-200",
  Unsatisfactory: "bg-red-100 text-red-700 border-red-200",
};

const goalStatusConfig: Record<string, { icon: typeof CheckCircle2; color: string; bg: string }> = {
  "Not Started": { icon: Clock, color: "text-gray-500", bg: "bg-gray-100 text-gray-600 border-gray-200" },
  "In Progress": { icon: TrendingUp, color: "text-blue-500", bg: "bg-blue-100 text-blue-600 border-blue-200" },
  Completed: { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-100 text-emerald-600 border-emerald-200" },
  Overdue: { icon: AlertTriangle, color: "text-red-500", bg: "bg-red-100 text-red-600 border-red-200" },
};

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props {
  employee: any;
  canManage: boolean;
}

export function ProfilePerformanceTab({ employee, canManage }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const employeeId = Number(employee.id);

  const [kpiDialog, setKpiDialog] = useState<{ open: boolean; editing: PerformanceKpi | null }>({ open: false, editing: null });
  const [goalDialog, setGoalDialog] = useState<{ open: boolean; editing: PerformanceGoal | null }>({ open: false, editing: null });
  const [reviewDialog, setReviewDialog] = useState<{ open: boolean; editing: PerformanceReview | null }>({ open: false, editing: null });

  const { data, isLoading } = useQuery({
    queryKey: ["performance", employeeId],
    queryFn: () => apiGetEmployeePerformance(employeeId),
  });

  const deleteKpiMutation = useMutation({
    mutationFn: (id: number) => apiDeleteKpi(employeeId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["performance", employeeId] });
      toast({ title: t("performance.manage.kpiDeleted") });
    },
    onError: () => toast({ title: t("common.error"), variant: "destructive" }),
  });

  const deleteGoalMutation = useMutation({
    mutationFn: (id: number) => apiDeleteGoal(employeeId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["performance", employeeId] });
      toast({ title: t("performance.manage.goalDeleted") });
    },
    onError: () => toast({ title: t("common.error"), variant: "destructive" }),
  });

  const deleteReviewMutation = useMutation({
    mutationFn: (id: number) => apiDeleteReview(employeeId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["performance", employeeId] });
      toast({ title: t("performance.manage.reviewDeleted") });
    },
    onError: () => toast({ title: t("common.error"), variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  const kpis = data?.kpis ?? [];
  const goals = data?.goals ?? [];
  const reviews = data?.reviews ?? [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["performance", employeeId] });

  return (
    <div className="space-y-5 max-w-4xl">

      {/* ── KPIs ── */}
      <Card className="shadow-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              {t("performance.kpiTitle")}
              {kpis.length > 0 && (
                <Badge variant="secondary" className="text-xs">{kpis.length}</Badge>
              )}
            </CardTitle>
            {canManage && (
              <Button size="sm" variant="outline" onClick={() => setKpiDialog({ open: true, editing: null })}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                {t("performance.manage.addKpi")}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {kpis.length === 0 ? (
            <p className="text-sm text-muted-foreground py-3 text-center italic">{t("performance.noKpis")}</p>
          ) : (
            <div className="space-y-2">
              {kpis.map((kpi) => (
                <KpiRow
                  key={kpi.id}
                  kpi={kpi}
                  canManage={canManage}
                  onEdit={() => setKpiDialog({ open: true, editing: kpi })}
                  onDelete={() => deleteKpiMutation.mutate(kpi.id)}
                  t={t}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Goals ── */}
      <Card className="shadow-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              {t("performance.goalsTitle")}
              {goals.length > 0 && (
                <Badge variant="secondary" className="text-xs">{goals.length}</Badge>
              )}
            </CardTitle>
            {canManage && (
              <Button size="sm" variant="outline" onClick={() => setGoalDialog({ open: true, editing: null })}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                {t("performance.manage.addGoal")}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {goals.length === 0 ? (
            <p className="text-sm text-muted-foreground py-3 text-center italic">{t("performance.noGoals")}</p>
          ) : (
            <div className="space-y-2">
              {goals.map((goal) => (
                <GoalRow
                  key={goal.id}
                  goal={goal}
                  canManage={canManage}
                  onEdit={() => setGoalDialog({ open: true, editing: goal })}
                  onDelete={() => deleteGoalMutation.mutate(goal.id)}
                  t={t}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Reviews ── */}
      <Card className="shadow-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Star className="h-4 w-4 text-amber-500" />
              {t("performance.reviewsTitle")}
              {reviews.length > 0 && (
                <Badge variant="secondary" className="text-xs">{reviews.length}</Badge>
              )}
            </CardTitle>
            {canManage && (
              <Button size="sm" variant="outline" onClick={() => setReviewDialog({ open: true, editing: null })}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                {t("performance.manage.addReview")}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {reviews.length === 0 ? (
            <p className="text-sm text-muted-foreground py-3 text-center italic">{t("performance.noReviews")}</p>
          ) : (
            <div className="space-y-2">
              {reviews.map((review) => (
                <ReviewRow
                  key={review.id}
                  review={review}
                  canManage={canManage}
                  onDelete={() => deleteReviewMutation.mutate(review.id)}
                  t={t}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Career Path ── */}
      <ProfileCareerPathSection employeeId={employeeId} canManage={canManage} />

      {/* ── Dialogs ── */}
      <KpiFormDialog
        open={kpiDialog.open}
        editing={kpiDialog.editing}
        employeeId={employeeId}
        onClose={() => setKpiDialog({ open: false, editing: null })}
        onSuccess={() => { invalidate(); setKpiDialog({ open: false, editing: null }); }}
      />
      <GoalFormDialog
        open={goalDialog.open}
        editing={goalDialog.editing}
        employeeId={employeeId}
        onClose={() => setGoalDialog({ open: false, editing: null })}
        onSuccess={() => { invalidate(); setGoalDialog({ open: false, editing: null }); }}
      />
      <ReviewFormDialog
        open={reviewDialog.open}
        editing={reviewDialog.editing}
        employeeId={employeeId}
        onClose={() => setReviewDialog({ open: false, editing: null })}
        onSuccess={() => { invalidate(); setReviewDialog({ open: false, editing: null }); }}
      />
    </div>
  );
}

// ─── Career Path Section (inside employee profile) ───────────────────────────

const OUTCOME_OPTIONS = [
  { value: "in_progress", labelKey: "performance.careerPath.outcomes.in_progress" },
  { value: "achieved", labelKey: "performance.careerPath.outcomes.achieved" },
  { value: "not_achieved", labelKey: "performance.careerPath.outcomes.not_achieved" },
  { value: "cancelled", labelKey: "performance.careerPath.outcomes.cancelled" },
];

const outcomeStyles: Record<string, string> = {
  achieved: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-400",
  not_achieved: "bg-red-100 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-400",
  in_progress: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-400",
  cancelled: "bg-zinc-100 text-zinc-600 border-zinc-200 dark:bg-zinc-500/15 dark:text-zinc-400",
};

function ProfileCareerPathSection({ employeeId, canManage }: { employeeId: number; canManage: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: entries = [], isLoading } = useQuery<CareerPath[]>({
    queryKey: ["career-path", employeeId],
    queryFn: () => apiGetEmployeeCareerPath(employeeId),
  });

  const { data: departments = [] } = useQuery<Department[]>({
    queryKey: ["departments"],
    queryFn: apiGetDepartments,
    staleTime: 5 * 60 * 1000,
  });

  // ── Add/Edit form state ────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<CareerPath | null>(null);
  const [form, setForm] = useState({ targetPosition: "", targetDepartment: "", targetTimeline: "", requiredSkills: "", developmentActions: "", notes: "" });

  const resetForm = () => setForm({ targetPosition: "", targetDepartment: "", targetTimeline: "", requiredSkills: "", developmentActions: "", notes: "" });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["career-path", employeeId] });

  const openAdd = () => { resetForm(); setEditing(null); setShowForm(true); };
  const openEdit = (e: CareerPath) => {
    setForm({ targetPosition: e.targetPosition, targetDepartment: e.targetDepartment ?? "", targetTimeline: e.targetTimeline ?? "", requiredSkills: e.requiredSkills ?? "", developmentActions: e.developmentActions ?? "", notes: e.notes ?? "" });
    setEditing(e);
    setShowForm(true);
  };

  const saveMutation = useMutation({
    mutationFn: () => editing ? apiUpdateCareerPath(employeeId, editing.id, form) : apiCreateCareerPath(employeeId, form),
    onSuccess: () => { invalidate(); toast({ title: editing ? t("performance.careerPath.updated") : t("performance.careerPath.added") }); setShowForm(false); setEditing(null); resetForm(); },
    onError: (err: any) => toast({ title: t("common.error"), description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDeleteCareerPath(employeeId, id),
    onSuccess: () => { invalidate(); toast({ title: t("performance.careerPath.deleted") }); },
    onError: (err: any) => toast({ title: t("common.error"), description: err.message, variant: "destructive" }),
  });

  // ── Outcome dialog state ───────────────────────────────────────────────────
  const [outcomeEntry, setOutcomeEntry] = useState<CareerPath | null>(null);
  const [outcomeForm, setOutcomeForm] = useState({ outcome: "", outcomeNotes: "" });

  const openOutcome = (e: CareerPath) => {
    setOutcomeForm({ outcome: e.outcome ?? "", outcomeNotes: e.outcomeNotes ?? "" });
    setOutcomeEntry(e);
  };
  const closeOutcome = () => { setOutcomeEntry(null); setOutcomeForm({ outcome: "", outcomeNotes: "" }); };

  const outcomeMutation = useMutation({
    mutationFn: () => apiUpdateCareerPath(employeeId, outcomeEntry!.id, {
      outcome: outcomeForm.outcome || undefined,
      outcomeNotes: outcomeForm.outcomeNotes || undefined,
    }),
    onSuccess: () => { invalidate(); toast({ title: t("performance.careerPath.outcomeRecorded") }); closeOutcome(); },
    onError: (err: any) => toast({ title: t("common.error"), description: err.message, variant: "destructive" }),
  });

  return (
    <Card className="shadow-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-primary" />
            {t("performance.careerPathTab")}
            {entries.length > 0 && <span className="text-xs bg-secondary text-secondary-foreground rounded-full px-2 py-0.5">{entries.length}</span>}
          </CardTitle>
          {canManage && (
            <Button size="sm" variant="outline" onClick={openAdd}>
              <Plus className="h-3.5 w-3.5 mr-1" /> {t("performance.careerPath.addEntry")}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">{[1, 2].map((i) => <div key={i} className="h-20 bg-muted rounded-lg animate-pulse" />)}</div>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground py-3 text-center italic">{t("performance.careerPath.noEntries")}</p>
        ) : (
          <div className="space-y-3">
            {entries.map((entry) => (
              <div key={entry.id} className="rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <ArrowUpCircle className="h-4 w-4 text-primary shrink-0" />
                    <div>
                      <p className="font-semibold text-sm">{entry.targetPosition}</p>
                      {entry.targetDepartment && <p className="text-xs text-muted-foreground">{entry.targetDepartment}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {entry.targetTimeline && (
                      <span className="text-xs border rounded-full px-2 py-0.5 flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> {entry.targetTimeline}
                      </span>
                    )}
                    {canManage && (
                      <>
                        <button
                          onClick={() => openOutcome(entry)}
                          title={entry.outcome ? t("performance.careerPath.updateOutcome") : t("performance.careerPath.recordOutcome")}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                            entry.outcome
                              ? `border ${outcomeStyles[entry.outcome] ?? "border-border text-muted-foreground"} hover:opacity-80`
                              : "border border-dashed border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 hover:bg-muted"
                          }`}
                        >
                          <ClipboardCheck className="h-3 w-3" />
                          {entry.outcome
                            ? t(`performance.careerPath.outcomes.${entry.outcome}`, entry.outcome)
                            : t("performance.careerPath.recordOutcome")}
                        </button>
                        <button onClick={() => openEdit(entry)} className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => deleteMutation.mutate(entry.id)} className="p-1.5 rounded text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                      </>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {entry.requiredSkills && (
                    <div className="rounded bg-blue-50 dark:bg-blue-500/10 p-2">
                      <p className="text-[10px] font-semibold text-blue-700 dark:text-blue-400 mb-0.5 flex items-center gap-1"><BookOpen className="h-3 w-3" />{t("performance.careerPath.requiredSkills")}</p>
                      <p className="text-xs text-blue-800 dark:text-blue-300 whitespace-pre-line">{entry.requiredSkills}</p>
                    </div>
                  )}
                  {entry.developmentActions && (
                    <div className="rounded bg-amber-50 dark:bg-amber-500/10 p-2">
                      <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 mb-0.5 flex items-center gap-1"><Lightbulb className="h-3 w-3" />{t("performance.careerPath.developmentActions")}</p>
                      <p className="text-xs text-amber-800 dark:text-amber-300 whitespace-pre-line">{entry.developmentActions}</p>
                    </div>
                  )}
                </div>
                {entry.notes && <p className="text-xs text-muted-foreground border-t pt-1.5 mt-1">{entry.notes}</p>}
                {entry.outcome && (
                  <div className="border-t pt-2 mt-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${outcomeStyles[entry.outcome] ?? ""}`}>
                        {t(`performance.careerPath.outcomes.${entry.outcome}`, entry.outcome)}
                      </span>
                      {entry.outcomeDate && (
                        <span className="text-[10px] text-muted-foreground">{new Date(entry.outcomeDate).toLocaleDateString()}</span>
                      )}
                    </div>
                    {entry.outcomeNotes && <p className="text-xs text-muted-foreground whitespace-pre-line">{entry.outcomeNotes}</p>}
                    {entry.outcomeSetByName && (
                      <p className="text-[10px] text-muted-foreground flex items-center gap-1"><User className="h-3 w-3" />{t("performance.careerPath.outcomeSetBy")}: {entry.outcomeSetByName}</p>
                    )}
                  </div>
                )}
                {entry.setByName && (
                  <p className="text-[10px] text-muted-foreground flex items-center gap-1"><User className="h-3 w-3" />{t("performance.careerPath.setBy")}: {entry.setByName}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Add/Edit dialog */}
      <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); setEditing(null); resetForm(); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-primary" />
              {editing ? t("performance.careerPath.editEntry") : t("performance.careerPath.addEntry")}
            </DialogTitle>
          </DialogHeader>
          <div className="pb-1">
            <FloatField
              label={t("performance.careerPath.targetPosition")}
              required
              value={form.targetPosition}
              onChange={(v) => setForm((f) => ({ ...f, targetPosition: v }))}
              placeholder="e.g. Senior Software Engineer"
            />
            <div className="flex gap-3">
              <FloatWrapper label={t("performance.careerPath.targetDepartment")} className="flex-1">
                <Select
                  value={form.targetDepartment || "__none__"}
                  onValueChange={(v) => setForm((f) => ({ ...f, targetDepartment: v === "__none__" ? "" : v }))}
                >
                  <SelectTrigger className={floatChildCls}>
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {departments.map((d) => (
                      <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FloatWrapper>
              <FloatField
                label={t("performance.careerPath.targetTimeline")}
                value={form.targetTimeline}
                onChange={(v) => setForm((f) => ({ ...f, targetTimeline: v }))}
                placeholder="e.g. Q3 2025"
              />
            </div>
            <FloatField
              label={t("performance.careerPath.requiredSkills")}
              type="textarea"
              value={form.requiredSkills}
              onChange={(v) => setForm((f) => ({ ...f, requiredSkills: v }))}
              placeholder="Skills and competencies needed…"
            />
            <FloatField
              label={t("performance.careerPath.developmentActions")}
              type="textarea"
              value={form.developmentActions}
              onChange={(v) => setForm((f) => ({ ...f, developmentActions: v }))}
              placeholder="Training, projects, or milestones…"
            />
            <FloatField
              label={t("performance.careerPath.notes")}
              type="textarea"
              value={form.notes}
              onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
              placeholder="Additional notes…"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditing(null); resetForm(); }}>{t("performance.careerPath.cancel")}</Button>
            <Button className="gradient-brand text-primary-foreground" onClick={() => saveMutation.mutate()} disabled={!form.targetPosition.trim() || saveMutation.isPending}>
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              {t("performance.careerPath.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Outcome dialog */}
      <Dialog open={!!outcomeEntry} onOpenChange={(v) => { if (!v) closeOutcome(); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4 text-primary" />
              {outcomeEntry?.outcome ? t("performance.careerPath.updateOutcome") : t("performance.careerPath.recordOutcome")}
            </DialogTitle>
            {outcomeEntry && (
              <p className="text-sm text-muted-foreground pt-0.5">
                {outcomeEntry.targetPosition}
                {outcomeEntry.targetDepartment && ` · ${outcomeEntry.targetDepartment}`}
              </p>
            )}
          </DialogHeader>
          <div className="pb-1">
            <FloatWrapper label={t("performance.careerPath.outcome")}>
              <Select
                value={outcomeForm.outcome || "__none__"}
                onValueChange={(v) => setOutcomeForm((f) => ({ ...f, outcome: v === "__none__" ? "" : v }))}
              >
                <SelectTrigger className={floatChildCls}>
                  <SelectValue placeholder={t("performance.careerPath.outcomePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— {t("performance.careerPath.outcomePlaceholder")} —</SelectItem>
                  {OUTCOME_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{t(o.labelKey)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FloatWrapper>
            <FloatField
              label={t("performance.careerPath.outcomeNotes")}
              type="textarea"
              value={outcomeForm.outcomeNotes}
              onChange={(v) => setOutcomeForm((f) => ({ ...f, outcomeNotes: v }))}
              placeholder={t("performance.careerPath.outcomeNotesPlaceholder")}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeOutcome}>{t("performance.careerPath.cancel")}</Button>
            <Button className="gradient-brand text-primary-foreground" onClick={() => outcomeMutation.mutate()} disabled={outcomeMutation.isPending}>
              {outcomeMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              {t("performance.careerPath.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Row Components ───────────────────────────────────────────────────────────

function KpiRow({ kpi, canManage, onEdit, onDelete, t }: {
  kpi: PerformanceKpi; canManage: boolean; onEdit: () => void; onDelete: () => void; t: any;
}) {
  const pct = kpi.targetValue > 0 ? Math.min(Math.round((kpi.currentValue / kpi.targetValue) * 100), 100) : 0;
  const barColor = pct >= 80 ? "from-emerald-500 to-green-400" : pct >= 50 ? "from-blue-500 to-cyan-400" : "from-orange-500 to-amber-400";
  const pctColor = pct >= 80 ? "text-emerald-600" : pct >= 50 ? "text-blue-600" : "text-orange-600";

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors">
      <div className="flex-1 space-y-2 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-foreground">{kpi.title}</span>
          <Badge variant="outline" className="text-[10px] shrink-0">{kpi.period}</Badge>
        </div>
        {kpi.description && <p className="text-xs text-muted-foreground line-clamp-2">{kpi.description}</p>}
        <div className="space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">{kpi.currentValue}{kpi.unit} / {kpi.targetValue}{kpi.unit}</span>
            <span className={`font-bold tabular-nums ${pctColor}`}>{pct}%</span>
          </div>
          <div className="relative h-2 rounded-full bg-muted overflow-hidden">
            <div className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r ${barColor} transition-all duration-500`} style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span><BarChart3 className="inline h-3 w-3 mr-1" />{t("performance.weight")}: {kpi.weight}</span>
          {kpi.createdByName && <span><User className="inline h-3 w-3 mr-1" />{kpi.createdByName}</span>}
        </div>
      </div>
      {canManage && (
        <div className="flex items-center gap-0.5 shrink-0">
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

function GoalRow({ goal, canManage, onEdit, onDelete, t }: {
  goal: PerformanceGoal; canManage: boolean; onEdit: () => void; onDelete: () => void; t: any;
}) {
  const config = goalStatusConfig[goal.status] || goalStatusConfig["Not Started"];
  const StatusIcon = config.icon;
  const barColor = goal.status === "Completed"
    ? "from-emerald-500 to-green-400"
    : goal.status === "Overdue"
    ? "from-red-500 to-rose-400"
    : "from-blue-500 to-cyan-400";

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors">
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${config.color} ${
        goal.status === "Completed" ? "bg-emerald-100" :
        goal.status === "Overdue" ? "bg-red-100" :
        goal.status === "In Progress" ? "bg-blue-100" : "bg-gray-100"
      }`}>
        <StatusIcon className="h-4 w-4" />
      </div>
      <div className="flex-1 space-y-2 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <span className="text-sm font-medium text-foreground">{goal.title}</span>
            {goal.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{goal.description}</p>}
          </div>
          <Badge variant="outline" className={`text-[10px] shrink-0 border ${config.bg}`}>{goal.status}</Badge>
        </div>
        <div className="space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">{t("performance.progress")}</span>
            <span className="font-semibold tabular-nums">{goal.progress}%</span>
          </div>
          <div className="relative h-2 rounded-full bg-muted overflow-hidden">
            <div className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r ${barColor} transition-all duration-500`} style={{ width: `${goal.progress}%` }} />
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
          {goal.dueDate && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(goal.dueDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
            </span>
          )}
          {goal.category && <Badge variant="secondary" className="text-[10px] h-4">{goal.category}</Badge>}
          {goal.createdByName && <span className="flex items-center gap-1 ml-auto"><User className="h-3 w-3" />{goal.createdByName}</span>}
        </div>
      </div>
      {canManage && (
        <div className="flex items-center gap-0.5 shrink-0">
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

function ReviewRow({ review, canManage, onDelete, t }: {
  review: PerformanceReview; canManage: boolean; onDelete: () => void; t: any;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors overflow-hidden">
      <div className="flex items-start gap-3 p-3 cursor-pointer" onClick={() => setExpanded((v) => !v)}>
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm shrink-0">
          <Star className="h-4 w-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-foreground">{review.reviewPeriod}</p>
              <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                <User className="h-3 w-3" />{review.reviewerName || "—"}
                <span className="mx-1">·</span>
                {new Date(review.reviewDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant="outline" className={`text-[10px] border ${ratingColorMap[review.ratingLabel] || ""}`}>
                {review.ratingLabel}
              </Badge>
              <span className="text-base font-bold tabular-nums">{review.overallRating.toFixed(1)}<span className="text-xs font-normal text-muted-foreground">/5</span></span>
            </div>
          </div>
          {/* Rating bar */}
          <div className="relative h-1.5 rounded-full bg-muted overflow-hidden mt-2">
            <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-amber-400 to-orange-500" style={{ width: `${(review.overallRating / 5) * 100}%` }} />
          </div>
          <div className="flex items-center gap-1 mt-1.5 text-xs text-muted-foreground">
            <ChevronRight className={`h-3 w-3 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`} />
            <span>{expanded ? "Collapse" : "Expand"}</span>
          </div>
        </div>
        {canManage && (
          <Button
            size="icon" variant="ghost"
            className="h-7 w-7 text-destructive hover:text-destructive shrink-0"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      {/* Expandable details */}
      <div className={`grid transition-all duration-300 ease-in-out ${expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="px-3 pb-3 pt-0 space-y-2 border-t border-border">
            {review.strengths && (
              <div className="p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 mt-2">
                <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mb-1">{t("performance.strengths")}</p>
                <p className="text-sm text-emerald-600 dark:text-emerald-300">{review.strengths}</p>
              </div>
            )}
            {review.improvements && (
              <div className="p-2.5 rounded-lg bg-orange-50 dark:bg-orange-500/10">
                <p className="text-xs font-semibold text-orange-700 dark:text-orange-400 mb-1">{t("performance.improvements")}</p>
                <p className="text-sm text-orange-600 dark:text-orange-300">{review.improvements}</p>
              </div>
            )}
            {review.comments && (
              <div className="p-2.5 rounded-lg bg-blue-50 dark:bg-blue-500/10">
                <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-1">{t("performance.comments")}</p>
                <p className="text-sm text-blue-600 dark:text-blue-300">{review.comments}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── KPI Form Dialog ──────────────────────────────────────────────────────────

function KpiFormDialog({ open, editing, employeeId, onClose, onSuccess }: {
  open: boolean; editing: PerformanceKpi | null; employeeId: number;
  onClose: () => void; onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const isEditing = editing !== null;

  const [form, setForm] = useState({ title: "", description: "", targetValue: "100", currentValue: "0", unit: "%", weight: 1, period: "Q2 2026" });

  useEffect(() => {
    if (open) {
      setForm(isEditing ? {
        title: editing!.title,
        description: editing!.description ?? "",
        targetValue: String(editing!.targetValue),
        currentValue: String(editing!.currentValue),
        unit: editing!.unit,
        weight: editing!.weight,
        period: editing!.period,
      } : { title: "", description: "", targetValue: "100", currentValue: "0", unit: "%", weight: 1, period: "Q2 2026" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const mutation = useMutation({
    mutationFn: () => isEditing
      ? apiUpdateKpi(employeeId, editing!.id, { title: form.title, description: form.description || null, targetValue: Number(form.targetValue), currentValue: Number(form.currentValue), unit: form.unit, weight: form.weight, period: form.period })
      : apiCreateKpi(employeeId, { title: form.title, description: form.description || undefined, targetValue: Number(form.targetValue), currentValue: Number(form.currentValue), unit: form.unit, weight: form.weight, period: form.period }),
    onSuccess: () => { toast({ title: t(isEditing ? "performance.manage.kpiUpdated" : "performance.manage.kpiCreated") }); onSuccess(); },
    onError: () => toast({ title: t("common.error"), variant: "destructive" }),
  });

  const isValid = form.title.trim() !== "" && Number(form.targetValue) > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(isEditing ? "performance.manage.editKpi" : "performance.manage.addKpi")}</DialogTitle>
        </DialogHeader>
        <div className="pb-1">
          <FloatField
            label={`${t("performance.manage.title")} *`}
            value={form.title}
            onChange={(v) => setForm((f) => ({ ...f, title: v }))}
            placeholder="e.g. Sales Revenue"
          />
          <FloatField
            label={t("performance.manage.description")}
            type="textarea"
            value={form.description}
            onChange={(v) => setForm((f) => ({ ...f, description: v }))}
            placeholder="Optional description…"
          />
          <div className="flex gap-3">
            <FloatField
              label={`${t("performance.manage.targetValue")} *`}
              value={form.targetValue}
              onChange={(v) => setForm((f) => ({ ...f, targetValue: v }))}
              placeholder="100"
              className="flex-[2]"
            />
            <FloatField
              label={t("performance.manage.unit")}
              value={form.unit}
              onChange={(v) => setForm((f) => ({ ...f, unit: v }))}
              placeholder="%"
            />
          </div>
          <div className="flex gap-3">
            <FloatField
              label={t("performance.manage.currentValue")}
              value={form.currentValue}
              onChange={(v) => setForm((f) => ({ ...f, currentValue: v }))}
              placeholder="0"
            />
            <FloatField
              label={t("performance.manage.period")}
              value={form.period}
              onChange={(v) => setForm((f) => ({ ...f, period: v }))}
              placeholder="Q2 2026"
            />
          </div>
          <div className="mt-4 space-y-2 px-1">
            <Label className="text-sm font-semibold">{t("performance.manage.weight")}: {form.weight}</Label>
            <Slider min={1} max={5} step={1} value={[form.weight]} onValueChange={([v]) => setForm((f) => ({ ...f, weight: v }))} />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Low</span><span>High</span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button className="gradient-brand text-primary-foreground" onClick={() => mutation.mutate()} disabled={!isValid || mutation.isPending}>
            {mutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {t(isEditing ? "common.save" : "performance.manage.addKpi")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Goal Form Dialog ─────────────────────────────────────────────────────────

function GoalFormDialog({ open, editing, employeeId, onClose, onSuccess }: {
  open: boolean; editing: PerformanceGoal | null; employeeId: number;
  onClose: () => void; onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const isEditing = editing !== null;

  const [form, setForm] = useState({ title: "", description: "", dueDate: "", status: "Not Started", progress: 0, category: "" });

  useEffect(() => {
    if (open) {
      setForm(isEditing ? {
        title: editing!.title,
        description: editing!.description ?? "",
        dueDate: editing!.dueDate ? editing!.dueDate.slice(0, 10) : "",
        status: editing!.status,
        progress: editing!.progress,
        category: editing!.category ?? "",
      } : { title: "", description: "", dueDate: "", status: "Not Started", progress: 0, category: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const mutation = useMutation({
    mutationFn: () => isEditing
      ? apiUpdateGoal(employeeId, editing!.id, { title: form.title, description: form.description || null, dueDate: form.dueDate || null, status: form.status, progress: form.progress, category: form.category || null })
      : apiCreateGoal(employeeId, { title: form.title, description: form.description || undefined, dueDate: form.dueDate || undefined, status: form.status, progress: form.progress, category: form.category || undefined }),
    onSuccess: () => { toast({ title: t(isEditing ? "performance.manage.goalUpdated" : "performance.manage.goalCreated") }); onSuccess(); },
    onError: () => toast({ title: t("common.error"), variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(isEditing ? "performance.manage.editGoal" : "performance.manage.addGoal")}</DialogTitle>
        </DialogHeader>
        <div className="pb-1">
          <FloatField
            label={`${t("performance.manage.title")} *`}
            value={form.title}
            onChange={(v) => setForm((f) => ({ ...f, title: v }))}
            placeholder="e.g. Improve customer satisfaction"
          />
          <FloatField
            label={t("performance.manage.description")}
            type="textarea"
            value={form.description}
            onChange={(v) => setForm((f) => ({ ...f, description: v }))}
            placeholder="Optional description…"
          />
          <div className="flex gap-3">
            <FloatWrapper label={t("performance.dueDate")}>
              <DatePicker
                value={form.dueDate}
                onChange={(v) => setForm((f) => ({ ...f, dueDate: v }))}
                className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm"
              />
            </FloatWrapper>
            <FloatField
              label={t("performance.category")}
              value={form.category}
              onChange={(v) => setForm((f) => ({ ...f, category: v }))}
              placeholder="e.g. Sales, Leadership"
            />
          </div>
          <FloatWrapper label={t("performance.status")}>
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger className={floatChildCls}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Not Started">{t("performance.notStarted")}</SelectItem>
                <SelectItem value="In Progress">{t("performance.inProgress")}</SelectItem>
                <SelectItem value="Completed">{t("performance.completed")}</SelectItem>
                <SelectItem value="Overdue">{t("performance.overdue")}</SelectItem>
              </SelectContent>
            </Select>
          </FloatWrapper>
          <div className="mt-4 space-y-2 px-1">
            <Label className="text-sm font-semibold">{t("performance.progress")}: {form.progress}%</Label>
            <Slider min={0} max={100} step={5} value={[form.progress]} onValueChange={([v]) => setForm((f) => ({ ...f, progress: v }))} />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0%</span><span>100%</span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button className="gradient-brand text-primary-foreground" onClick={() => mutation.mutate()} disabled={!form.title.trim() || mutation.isPending}>
            {mutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {t(isEditing ? "common.save" : "performance.manage.addGoal")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Review Form Dialog ───────────────────────────────────────────────────────

function ReviewFormDialog({ open, editing, employeeId, onClose, onSuccess }: {
  open: boolean; editing: PerformanceReview | null; employeeId: number;
  onClose: () => void; onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const isEditing = editing !== null;

  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ reviewPeriod: "", reviewDate: today, overallRating: 3, strengths: "", improvements: "", comments: "" });

  useEffect(() => {
    if (open) {
      setForm(isEditing ? {
        reviewPeriod: editing!.reviewPeriod,
        reviewDate: editing!.reviewDate ? editing!.reviewDate.slice(0, 10) : today,
        overallRating: Math.round(editing!.overallRating),
        strengths: editing!.strengths ?? "",
        improvements: editing!.improvements ?? "",
        comments: editing!.comments ?? "",
      } : { reviewPeriod: "", reviewDate: today, overallRating: 3, strengths: "", improvements: "", comments: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const ratingLabel = RATING_LABELS[form.overallRating] || "Meets Expectations";

  const mutation = useMutation({
    mutationFn: () => isEditing
      ? apiUpdateReview(employeeId, editing!.id, { reviewPeriod: form.reviewPeriod, overallRating: form.overallRating, ratingLabel, strengths: form.strengths || null, improvements: form.improvements || null, comments: form.comments || null, reviewDate: form.reviewDate })
      : apiCreateReview(employeeId, { reviewPeriod: form.reviewPeriod, overallRating: form.overallRating, ratingLabel, strengths: form.strengths || undefined, improvements: form.improvements || undefined, comments: form.comments || undefined, reviewDate: form.reviewDate }),
    onSuccess: () => { toast({ title: t(isEditing ? "performance.manage.reviewUpdated" : "performance.manage.reviewCreated") }); onSuccess(); },
    onError: () => toast({ title: t("common.error"), variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t(isEditing ? "performance.manage.editReview" : "performance.manage.addReview")}</DialogTitle>
        </DialogHeader>
        <div className="pb-1">
          <div className="flex gap-3">
            <FloatField
              label={`${t("performance.reviewPeriod")} *`}
              value={form.reviewPeriod}
              onChange={(v) => setForm((f) => ({ ...f, reviewPeriod: v }))}
              placeholder="e.g. Q1 2026"
            />
            <FloatWrapper label={t("performance.reviewDate")}>
              <DatePicker
                value={form.reviewDate}
                onChange={(v) => setForm((f) => ({ ...f, reviewDate: v }))}
                className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm"
              />
            </FloatWrapper>
          </div>

          {/* Star rating */}
          <div className="mt-4 space-y-2 px-1">
            <Label className="text-sm font-semibold">{t("performance.rating")}</Label>
            <div className="flex items-center gap-1.5">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, overallRating: star }))}
                  className="p-0.5 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <Star className={`h-7 w-7 transition-colors ${star <= form.overallRating ? "text-amber-400 fill-amber-400" : "text-muted-foreground/30"}`} />
                </button>
              ))}
            </div>
            <Badge variant="outline" className={`text-xs border ${ratingColorMap[ratingLabel] || ""}`}>{ratingLabel}</Badge>
          </div>

          <FloatField
            label={t("performance.strengths")}
            type="textarea"
            value={form.strengths}
            onChange={(v) => setForm((f) => ({ ...f, strengths: v }))}
            placeholder="Key strengths observed…"
          />
          <FloatField
            label={t("performance.improvements")}
            type="textarea"
            value={form.improvements}
            onChange={(v) => setForm((f) => ({ ...f, improvements: v }))}
            placeholder="Areas for improvement…"
          />
          <FloatField
            label={t("performance.comments")}
            type="textarea"
            value={form.comments}
            onChange={(v) => setForm((f) => ({ ...f, comments: v }))}
            placeholder="Additional comments…"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button className="gradient-brand text-primary-foreground" onClick={() => mutation.mutate()} disabled={!form.reviewPeriod.trim() || mutation.isPending}>
            {mutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {t(isEditing ? "common.save" : "performance.manage.addReview")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
